/** 调用普通 Options API 方法或 script setup 明确公开的菜单生命周期方法。 */
export function callPageMethod(instance, name, ...args) {
  if (!instance) return undefined
  const exposed = instance.$?.exposed
  const method = exposed?.[name] || instance[name]
  return typeof method === 'function' ? method.apply(instance, args) : undefined
}

/** 包装页面仅委托一个明确命名的既有共享组件，不复制业务状态。 */
export function findPageChild(instance, name) {
  const visited = new Set()
  const visit = (vnode) => {
    if (!vnode || visited.has(vnode)) return null
    visited.add(vnode)
    const component = vnode.component
    if (component) {
      const componentName = component.type?.name || component.type?.__name
      if (componentName === name) return component.proxy
      const nested = visit(component.subTree)
      if (nested) return nested
    }
    if (Array.isArray(vnode.children)) {
      for (const child of vnode.children) {
        const nested = visit(child)
        if (nested) return nested
      }
    }
    return null
  }
  return visit(instance?.$?.subTree)
}

/** 仅关闭策略中明确登记的弹窗字段，保留列表条件与分页。 */
export function closePageTransients(instance, paths = []) {
  for (const path of paths) {
    const keys = path.split('.')
    const field = keys.pop()
    const target = keys.reduce((current, key) => current?.[key], instance)
    if (target && typeof target[field] === 'boolean') target[field] = false
  }
}

/** 在发起请求的同步边界捕获当前页面代次，供多步初始化继续复用。 */
export function requestPageRead(instance, starter, options) {
  return typeof instance?.$pageRequest === 'function'
    ? instance.$pageRequest(starter, options)
    : starter()
}

/** 缓存中的路由监听只有在自己的目标页面可见时才能触发业务查询。 */
export function isPageRouteActive(instance, path) {
  const route = instance?.$router?.currentRoute?.value || instance?.$route
  return String(route?.path || '').toLowerCase() === path.toLowerCase()
    && instance?.$pageIsActive?.() !== false
}

/** 列表与详情共用组件时，失活实例继续读取自己的路由快照。 */
export function getPageRoute(instance) {
  return instance?.$pageReadScope?.route || instance?.$route
}

const readPath = (instance, path) => path.split('.').reduce((value, key) => value?.[key], instance)

/** 仅对显式登记的分页控件纠正越界页，每个表至多补读一次。 */
export async function correctPageAfterRefresh(instance, entries = [], isCurrent = () => instance?.$pageIsActive?.() !== false) {
  for (const entry of entries) {
    if (!isCurrent()) return
    const current = Number(readPath(instance, entry.page))
    const size = Number(readPath(instance, entry.size))
    const total = Number(readPath(instance, entry.total))
    if (!Number.isFinite(current) || !Number.isFinite(size) || size <= 0 || !Number.isFinite(total) || total < 0) continue
    const lastPage = Math.max(1, Math.ceil(total / size))
    if (current <= lastPage) continue
    const keys = entry.page.split('.')
    const field = keys.pop()
    const parent = keys.reduce((value, key) => value?.[key], instance)
    if (!parent) continue
    parent[field] = lastPage
    await callPageMethod(instance, entry.method, ...(Array.isArray(entry.args) ? entry.args : [lastPage]))
  }
}
